import { useRef, useEffect, useState, useCallback } from "react";
import Header from "@/components/Header";
import axios from "axios";
import { AssistantBubble } from "@/components/chat/AssistantBubble";
import { API } from "@/components/chat/chatShared";
import ChatInput from "@/components/ChatInput";
import OrdersPanel from "@/components/OrdersPanel";
import FamilyPanel from "@/components/FamilyPanel";
import ScamAlertsPanel from "@/components/ScamAlertsPanel";
import MCPPanel from "@/components/MCPPanel";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import SwiggyLinkBanner from "@/components/SwiggyLinkBanner";
import LoginModal from "@/components/LoginModal";
import MyProfileModal from "@/components/MyProfileModal";
import FamilyDashboard from "@/components/FamilyDashboard";
import { getStoredProfile, logout, isAuthed } from "@/lib/auth";

export default function ChatScreen({
  messages,
  isLoading,
  onSendMessage,
  onSendButton,
  onSendLocation,
  onSendVoice,
  onSendImage,
  sessionId,
}) {
  const chatEndRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [showOrders, setShowOrders] = useState(false);
  const [showFamily, setShowFamily] = useState(false);
  const [showScamAlerts, setShowScamAlerts] = useState(false);
  const [showMCP, setShowMCP] = useState(false);
  const [profile, setProfile] = useState(() => getStoredProfile());
  const [showLogin, setShowLogin] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const refreshOrders = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/orders/${encodeURIComponent(`web:${sessionId}`)}`);
      setOrders(res.data.orders || []);
    } catch (_) {}
  }, [sessionId]);

  const lastInIdx = messages.map((m) => m.direction).lastIndexOf("in");

  if (profile?.role === "family") {
    return <FamilyDashboard profile={profile} onLogout={async () => { await logout(); setProfile(null); }} />;
  }

  return (
    <div className="flex flex-col h-full" data-testid="chat-screen">
      <Header
        orderCount={orders.length}
        sessionId={sessionId}
        onShowOrders={() => {
          refreshOrders();
          setShowOrders(true); setShowFamily(false); setShowScamAlerts(false); setShowMCP(false);
        }}
        onShowFamily={() => { setShowFamily(true); setShowOrders(false); setShowScamAlerts(false); setShowMCP(false); }}
        onShowScamAlerts={() => { setShowScamAlerts(true); setShowOrders(false); setShowFamily(false); setShowMCP(false); }}
        onShowMCP={() => { setShowMCP(true); setShowOrders(false); setShowFamily(false); setShowScamAlerts(false); }}
        onSOSTriggered={() => onSendMessage("SOS")}
        profile={profile}
        onLoginClick={() => setShowLogin(true)}
        onLogout={async () => { await logout(); setProfile(null); }}
        onEditProfileClick={() => setShowMyProfile(true)}
        onMyProfileClick={() => setShowMyProfile(true)}
      />

      {/* Announcement Banner */}
      <AnnouncementBanner />
      <WhatsAppCTA />
      <SwiggyLinkBanner sessionId={sessionId} profile={profile} />

      {/* Chat area */}
      <div
        className="flex-1 overflow-y-auto chat-scroll chat-bg px-3 py-4 md:px-6 md:py-6"
        style={{ paddingBottom: "80px" }}
        data-testid="chat-area"
      >
        <div className="flex flex-col gap-3 max-w-3xl mx-auto w-full">
          {messages.map((msg, i) => (
            <AssistantBubble
              key={msg.id}
              message={msg}
              active={!isLoading && i > lastInIdx}
              onButton={onSendButton}
              onLocation={onSendLocation}
            />
          ))}

          {isLoading && (
            <div className="flex justify-start message-enter" data-testid="typing-indicator">
              <div className="bubble-received shadow-sm px-4 py-3 flex items-center gap-2 w-fit">
                <div className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span className="text-sm text-gray-500">Elderra is typing…</span>
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Input area */}
      <ChatInput
        onSendMessage={onSendMessage}
        onSendVoice={onSendVoice}
        onSendImage={onSendImage}
        onSendLocation={onSendLocation}
        isLoading={isLoading}
        sessionId={sessionId}
      />

      {/* Panels */}
      {showOrders && (
        <OrdersPanel
          orders={orders}
          onClose={() => setShowOrders(false)}
          sessionId={sessionId}
          onMessageUpdate={() => {}}
          onReorder={() => refreshOrders()}
          onOrdersChanged={refreshOrders}
        />
      )}

      {showFamily && (
        <FamilyPanel
          sessionId={sessionId}
          profile={profile}
          onClose={() => setShowFamily(false)}
        />
      )}

      {showScamAlerts && (
        <ScamAlertsPanel
          sessionId={sessionId}
          onClose={() => setShowScamAlerts(false)}
        />
      )}

      {showMCP && (
        <MCPPanel onClose={() => setShowMCP(false)} />
      )}

      {showLogin && (
        <LoginModal
          sessionId={sessionId}
          onClose={() => setShowLogin(false)}
          onSuccess={(prof) => { setProfile(prof); setShowLogin(false); }}
        />
      )}

      {showMyProfile && (
        <MyProfileModal
          sessionId={sessionId}
          onClose={() => setShowMyProfile(false)}
          onSaved={(prof) => { if (isAuthed() && prof) setProfile(prof); }}
        />
      )}
    </div>
  );
}
